import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  Link as MuiLink,
  Stack,
  Typography
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  API_RESPONSE_CODE,
  BRAND_COLOR,
  BRAND_HOVER,
  BusinessType,
  ONBOARDING_DIALOG_HEADER_BG,
  ONBOARDING_DIALOG_HEADER_BORDER,
  GUIDANCE_CALLOUT_PANEL_BG,
  GUIDANCE_CALLOUT_PANEL_BORDER,
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_ROLES_AND_ACCESS,
  useKivunovaTranslation,
  usePlatformConfig
} from "@kivunova/kivufrontendcommon";
import { RootState } from "../../store/store";
import { UserRole } from "../Profile/enums/profileEnums";
import { useGetCompanyStores } from "../../hooks/api/useStore";
import AddStoreModal from "./AddStoreModal";

const HIGHLIGHTS = [
  {
    Icon: Inventory2OutlinedIcon,
    titleKey: "no-store-onboarding-highlight-inventory-title",
    titleFallback: "Everything in one place",
    descKey: "no-store-onboarding-highlight-inventory-desc",
    descFallback:
      "Inventory, orders, customers, suppliers, and staff can all link to a store."
  },
  {
    Icon: HubOutlinedIcon,
    titleKey: "no-store-onboarding-highlight-branches-title",
    titleFallback: "Grow across locations",
    descKey: "no-store-onboarding-highlight-branches-desc",
    descFallback:
      "Run multiple shops or branches under one company when you scale."
  },
  {
    Icon: RocketLaunchOutlinedIcon,
    titleKey: "no-store-onboarding-highlight-unlock-title",
    titleFallback: "Unlock what’s next",
    descKey: "no-store-onboarding-highlight-unlock-desc",
    descFallback:
      "Add categories, products, record stock, and invite your team once your store exists."
  }
] as const;

/**
 * Prompts company admins to create a store when the company has none yet.
 * Stores anchor inventory, staff, customers, and orders; the platform supports multiple stores per company.
 */
const NoStoreOnboardingDialog: React.FC = () => {
  const theme = useTheme();
  const { t } = useKivunovaTranslation();
  const { config, isLoading: isBusinessTypeLoading } = usePlatformConfig();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const storeList = useSelector((state: RootState) => state.store.storeList);
  const getCompanyStores = useGetCompanyStores();

  const tenantBusinessType = config?.businessType ?? null;
  const isEducationTenant = tenantBusinessType === BusinessType.EDUCATION;
  const isStoreGateEligible =
    !isBusinessTypeLoading && tenantBusinessType != null && !isEducationTenant;

  const [storesLoaded, setStoresLoaded] = useState(false);
  const [addStoreOpen, setAddStoreOpen] = useState(false);

  const isCompanyAdmin =
    String(userDetails?.role ?? "").trim() === UserRole.COMPANY_ADMIN;
  const companyId = userDetails?.companyId?.trim() ?? "";

  useEffect(() => {
    if (!isStoreGateEligible || !isCompanyAdmin || !companyId) {
      setStoresLoaded(false);
      return;
    }

    let cancelled = false;
    setStoresLoaded(false);

    getCompanyStores
      .mutateAsync({ companyId })
      .then((response) => {
        if (!cancelled && response?.listStores?.code === API_RESPONSE_CODE.OK) {
          setStoresLoaded(true);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [isStoreGateEligible, isCompanyAdmin, companyId]);

  const rolesAndAccessPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_ROLES_AND_ACCESS, language, defaultLang)
      : `/${PAGE_ROUTE_ROLES_AND_ACCESS}`;

  const hasStores = (storeList ?? []).length > 0;
  const dialogOpen =
    isStoreGateEligible &&
    isCompanyAdmin &&
    storesLoaded &&
    !hasStores &&
    !addStoreOpen;

  const handleCreateStore = () => {
    setAddStoreOpen(true);
  };

  const handleAddStoreClose = () => {
    setAddStoreOpen(false);
  };

  return (
    <>
      <Dialog
        open={dialogOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        data-testid="no-store-onboarding-dialog"
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(6px)",
              backgroundColor: alpha(theme.palette.common.black, 0.45)
            }
          },
          paper: {
            sx: {
              borderRadius: 3,
              overflow: "hidden",
              maxHeight: "min(90vh, 640px)",
              display: "flex",
              flexDirection: "column",
              boxShadow: `0 24px 64px ${alpha(BRAND_COLOR, 0.22)}`,
              border: `1px solid ${alpha(BRAND_COLOR, 0.12)}`
            }
          }
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "hidden"
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              px: 2.5,
              pt: 2.5,
              pb: 2,
              textAlign: "center",
              background:
                ONBOARDING_DIALOG_HEADER_BG ??
                "linear-gradient(180deg, #edf6fc 0%, #e8f4fc 100%)",
              borderBottom: `1px solid ${ONBOARDING_DIALOG_HEADER_BORDER ?? "rgba(25, 118, 210, 0.2)"}`
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                mx: "auto",
                mb: 1.25,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(BRAND_COLOR, 0.1),
                border: `1px solid ${alpha(BRAND_COLOR, 0.14)}`,
                color: BRAND_COLOR
              }}
            >
              <StorefrontOutlinedIcon sx={{ fontSize: 17 }} />
            </Box>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color="text.primary"
              sx={{
                letterSpacing: "-0.02em",
                lineHeight: 1.3,
                fontSize: "1.05rem"
              }}
            >
              {t(
                "no-store-onboarding-title",
                "Create your first store to get started"
              )}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.75,
                maxWidth: 360,
                mx: "auto",
                fontSize: "0.8125rem"
              }}
            >
              {t(
                "no-store-onboarding-subtitle",
                "Stores are the foundation of Kivunova FMS — set yours up in minutes."
              )}
            </Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              px: 2.5,
              py: 2,
              bgcolor: "background.paper"
            }}
          >
            <Stack spacing={1.25}>
              {HIGHLIGHTS.map(
                (
                  { Icon, titleKey, titleFallback, descKey, descFallback },
                  index
                ) => (
                  <Box
                    key={titleKey}
                    component={motion.div}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 * (index + 1), duration: 0.3 }}
                    sx={{
                      display: "flex",
                      gap: 1.25,
                      alignItems: "flex-start",
                      p: 1.25,
                      borderRadius: 2,
                      bgcolor: alpha(BRAND_COLOR, 0.04),
                      border: `1px solid ${alpha(BRAND_COLOR, 0.08)}`
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        flexShrink: 0,
                        borderRadius: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: alpha(BRAND_COLOR, 0.1),
                        color: BRAND_COLOR
                      }}
                    >
                      <Icon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ fontSize: "0.8125rem", lineHeight: 1.35 }}
                      >
                        {t(titleKey, titleFallback)}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.25, fontSize: "0.75rem", lineHeight: 1.4 }}
                      >
                        {t(descKey, descFallback)}
                      </Typography>
                    </Box>
                  </Box>
                )
              )}
            </Stack>

            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: 2,
                bgcolor: GUIDANCE_CALLOUT_PANEL_BG ?? "#ffffff",
                border: `1px solid ${GUIDANCE_CALLOUT_PANEL_BORDER ?? "#e8f4fc"}`
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.75rem", lineHeight: 1.45 }}
              >
                {t(
                  "no-store-onboarding-roles-prefix",
                  "Wondering who can manage stores?"
                )}{" "}
                <MuiLink
                  component={Link}
                  to={rolesAndAccessPath}
                  underline="hover"
                  sx={{ color: BRAND_COLOR, fontWeight: 600 }}
                  data-testid="no-store-onboarding-roles-link"
                >
                  {t(
                    "no-store-onboarding-roles-link",
                    "Learn about roles and access"
                  )}
                </MuiLink>
                {t(
                  "no-store-onboarding-roles-suffix",
                  " to see who can invite employees."
                )}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              px: 2.5,
              pb: 2,
              pt: 1.5,
              bgcolor: "background.paper",
              borderTop: `1px solid ${alpha(BRAND_COLOR, 0.08)}`
            }}
          >
            <Stack direction="row" spacing={1}>
              <Button
                size="medium"
                onClick={handleCreateStore}
                variant="contained"
                data-testid="no-store-onboarding-create-store"
                sx={{
                  flex: 1,
                  py: 0.65,
                  backgroundColor: BRAND_COLOR,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  boxShadow: `0 2px 8px ${alpha(BRAND_COLOR, 0.28)}`,
                  "&:hover": {
                    backgroundColor: BRAND_HOVER,
                    boxShadow: `0 3px 12px ${alpha(BRAND_COLOR, 0.32)}`
                  }
                }}
              >
                {t("no-store-onboarding-create-store", "Create store")}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Dialog>

      <AddStoreModal open={addStoreOpen} onClose={handleAddStoreClose} />
    </>
  );
};

export default NoStoreOnboardingDialog;
